import './JobSourcingSettingsModal.scss';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { usePatchItem } from 'services/common/react-query-common-service';

interface JobSourcingSettingsModalProps {
  open: boolean;
  onClose: () => void;
  jobData: any;
  refetch: () => void;
}

const JobSourcingSettingsModal: React.FC<JobSourcingSettingsModalProps> = ({
  open,
  onClose,
  jobData,
  refetch,
}) => {
  const [allowJobBoard, setAllowJobBoard] = useState(
    jobData?.allowJobBoardSearch ?? false,
  );
  const [allowEmailCampaign, setAllowEmailCampaign] = useState(
    jobData?.allowEmailCampaign ?? false,
  );

  const { mutateAsync: patchItem, isPending } = usePatchItem();

  const handleSave = async () => {
    try {
      await patchItem({
        data: {
          allowEmailCampaign: allowEmailCampaign,
          allowJobBoardSearch: allowJobBoard,
        },
        endpoint: 'jobs/settings',
        id: jobData?.id,
      });
      refetch();
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="job-sourcing-settings-dialog"
      PaperProps={{
        className: 'settings-dialog-paper',
      }}>
      <DialogTitle className="settings-dialog-title">
        <Box className="title-container">
          <Typography variant="h6">Job Sourcing Settings</Typography>
          <IconButton onClick={onClose} size="small" className="close-button">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" className="title-subtitle">
          Control which sourcing options your recruiters can use for this job.
          You can always run searches yourself as Team Lead.
        </Typography>
      </DialogTitle>

      <DialogContent dividers className="settings-dialog-content">
        <Box className="setting-row">
          <Switch
            checked={allowJobBoard}
            onChange={(e) => setAllowJobBoard(e.target.checked)}
            color="primary"
          />
          <Box className="setting-text">
            <Typography className="setting-label">
              Allow Job Board Search for Recruiters
            </Typography>
            <Typography variant="caption" className="setting-description">
              Let recruiters run job board searches (Dice, Monster) for this
              job.
            </Typography>
          </Box>
        </Box>

        <Box className="setting-row">
          <Switch
            checked={allowEmailCampaign}
            onChange={(e) => setAllowEmailCampaign(e.target.checked)}
            color="primary"
          />
          <Box className="setting-text">
            <Typography className="setting-label">
              Allow Subcontractor Campaign for Recruiters
            </Typography>
            <Typography variant="caption" className="setting-description">
              Let recruiters trigger subcontractor email campaigns for this job.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="settings-dialog-actions">
        <Button onClick={onClose} className="cancel-button">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isPending}
          className="save-button">
          {isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobSourcingSettingsModal;
